import React, { useEffect, useState, useRef } from "react";
import { ReactComponent as PostIcon } from "./icons/post-icon.svg";
import { ReactComponent as VideoIcon } from "./icons/videos-icon.svg";
import { ReactComponent as ArticleIcon } from "./icons/article-icon.svg";
import { ReactComponent as PostIconGray } from "./icons/post-gray-icon.svg";
import { ReactComponent as VideoIconPink } from "./icons/video-pink.svg";
import { ReactComponent as ArticleIconPink } from "./icons/article-pink.svg";
import PlusIcon from "./icons/plus-circle.svg";
import Feeds from "./components/Feeds";
import VideosCard from "./components/Videos";
import Articles from "./components/Articles";
import PersonalValues from "./components/PersonalValues";
import ServicesSection from "./components/ServicesSection";
import ProjectsSection from "./components/ProjectsSection";
import EducationSection from "./components/EducationSection";
import AccomplishmentsSection from "./components/AccomplishmentsSection";
import SkillsSection from "./components/SkillsSection";
import ResumeSection from "./components/ResumeSection";
import SocialLinksSection from "./components/SocialLinksSection";
import RecommendationsSection from "./components/RecommendationsSection";
import CandidateInfo from "./components/CandidateInfo";
import ConnectionCard from "./components/ConnectionCard";
import ProfessionalPassions from "./components/ProfessionalPassions";
import WorkExperience from "./components/WorkExperience";
import ProfileConnections from "./components/ProfileConnections";
import ProfileProgressCard, {
  ProfileTaskKey,
} from "./components/ProfileProgressCard";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import appRoutes from "../../routes/app.routes";
import { rootReducersState } from "../../reducers";
import { videoStudioListRequest } from "../../reducers/candidate/videoStudio.reducer";
import Banner from "./components/Banner";
import { getProfileRecommendationsRequest } from "../../reducers/profileRecommendations/profileRecommendations.reducer";
import { connectionRequestStatus } from "../../utils/appConstants";
import profileRecommendationsServices from "../../services/profileRecommendations.services";
import { FlashMessage, Spinner } from "../common";
import { fetchCandidatePublicProfile } from "../../services/candidate.services";
import { getGenderSelectOption } from "../../utils/helper";
import NotFoundPage from "../ErrorPages/NotFound";
import { fetchSessionDataRequest } from "../../reducers/auth/session.reducer";
import { candidateViewPageUpdateFlagReset } from "../../reducers/candidate/candidate.reducer";

function NewProfileView() {
  const [activeTab, setActiveTab] = useState<string>("post");
  const navigate = useNavigate();

  const publicPage = !!useMatch(appRoutes.candidatePublicProfile.path);

  const params = useParams();
  const sessionReducer = useSelector(
    ({ session }: rootReducersState) => session
  );
  const sessionCurrentUser = useSelector(
    ({ session }: rootReducersState) => session.currentUser
  );

  const [tokenData, setTokenData] = useState<any>({});
  const [profileLoading, setProfileLoading] = useState(publicPage);
  const [profileNotFound, setProfileNotFound] = useState(false);
  const dispatch = useDispatch();
  const candidateViewPageUpdateFlag = useSelector(
    ({ candidate }: rootReducersState) =>
      candidate.candidates.candidateViewPageUpdateFlag
  );

  // Profile Recommendations state from Redux
  const profileRecommendationsSelector = useSelector(
    ({ profileRecommendations }: rootReducersState) => profileRecommendations
  );

  // Extract candidate recommendations from the response - updated for new structure
  const candidateRecommendations: any[] = _.get(
    profileRecommendationsSelector,
    "profileRecommendations.data.data.candidate_recommendations",
    []
  );


  useEffect(() => {
    if (!publicPage) {
      return;
    }

    const slug = _.get(params, "slug", "");
    setProfileLoading(true);
    setProfileNotFound(false);

    fetchCandidatePublicProfile(slug)
      .then((result) => {
        const checkData = _.get(result, "data", {});
        if (result.flag && !_.isEmpty(checkData)) {
          setTokenData(checkData);
        } else {
          setTokenData({});
          setProfileNotFound(true);
        }
      })
      .catch(() => {
        setTokenData({});
        setProfileNotFound(true);
      })
      .then(() => {
        setProfileLoading(false);
      });
    // eslint-disable-next-line
  }, [publicPage, params.slug]);

  /** Keep local profile snapshot in sync with Redux session after async fetch / updates */
  useEffect(() => {
    if (publicPage) return;
    if (!_.isEmpty(sessionCurrentUser)) {
      setTokenData(sessionCurrentUser);
    }
  }, [publicPage, sessionCurrentUser]);

  useEffect(() => {
    if (publicPage) return;
    if (candidateViewPageUpdateFlag === "success") {
      dispatch(candidateViewPageUpdateFlagReset());
      dispatch(fetchSessionDataRequest());
    }
  }, [candidateViewPageUpdateFlag, dispatch, publicPage]);

  // State to track which recommendations to show (only 3 at a time)
  const [visibleRecommendations, setVisibleRecommendations] = useState<any[]>(
    []
  );

  // Ref to track current visibleRecommendations
  const visibleRecommendationsRef = useRef<any[]>([]);
  const removedProfileIdsRef = useRef<number[]>([]);

  // Connection status tracking - now we can initialize with existing follow data
  const [connectionRequests, setConnectionRequests] = useState<{
    [key: number]: any;
  }>({});

  // Initialize visible recommendations and connection status from existing follow data
  useEffect(() => {
    if (candidateRecommendations.length > 0) {
      // Show only first 3 recommendations
      const initialVisible = candidateRecommendations.slice(0, 3);
      setVisibleRecommendations(initialVisible);

      const initialConnectionStatus: { [key: number]: any } = {};
      candidateRecommendations.forEach((recommendation: any) => {
        if (recommendation.follow) {
          // Map the status number to status text using connectionRequestStatus constants
          const matchedStatus = _.find(
            connectionRequestStatus,
            (status: any) => status.status_id === recommendation.follow.status
          );
          const statusText =
            (matchedStatus && matchedStatus.status_text) || "Connect";

          initialConnectionStatus[recommendation.id] = {
            connect_id: recommendation.follow.connect_id,
            status_id: recommendation.follow.status,
            status_text: statusText,
          };
        }
      });
      setConnectionRequests(initialConnectionStatus);
    }
  }, [candidateRecommendations]);

  // Handle connection status updates and remove profile when connection request is sent
  const handleConnectionUpdate = (id: number, status: any) => {
    setConnectionRequests((prev) => ({
      ...prev,
      [id]: status,
    }));

    // If connection request is sent (pending), remove this profile and show next one
    if (status.status_text === "pending") {
      setVisibleRecommendations((prev) => {
        // Remove the current profile
        const filtered = prev.filter((rec: any) => rec.id !== id);

        // Find the next available recommendation that's not already visible and not already processed
        const nextRecommendation = _.find(
          candidateRecommendations,
          (rec: any) =>
            !prev.some((visibleRec: any) => visibleRec.id === rec.id) &&
            rec.id !== id &&
            !connectionRequests[rec.id]
        );

        // Add the next recommendation if available
        if (nextRecommendation) {
          return [...filtered, nextRecommendation];
        }

        return filtered;
      });
    }
  };

  // Function to load more recommendations if we have less than 3 visible
  const loadMoreRecommendations = () => {
    if (visibleRecommendations.length < 3) {
      const nextRecommendation = _.find(
        candidateRecommendations,
        (rec: any) =>
          !visibleRecommendations.some(
            (visibleRec: any) => Number(visibleRec.id) === Number(rec.id)
          ) &&
          removedProfileIdsRef.current.indexOf(Number(rec.id)) === -1 &&
          !connectionRequests[rec.id]
      );

      if (nextRecommendation) {
        setVisibleRecommendations((prev) => [...prev, nextRecommendation]);
      } else {
        console.log("No more recommendations available to load");
      }
    }
  };

  // Handle profile removal
  const handleProfileRemove = (profileId: number) => {
    profileRecommendationsServices
      .removeProfileRecommendation(profileId)
      .then((response) => {
        if (response.flag) {
          const removedId = Number(profileId);
          if (removedProfileIdsRef.current.indexOf(removedId) === -1) {
            removedProfileIdsRef.current = [
              ...removedProfileIdsRef.current,
              removedId,
            ];
          }

          const updatedRecommendations =
            visibleRecommendationsRef.current.filter(
              (rec: any) => Number(rec.id) !== removedId
            );
          const nextRecommendation = _.find(
            candidateRecommendations,
            (rec: any) => {
              const recId = Number(rec.id);
              return (
                recId !== removedId &&
                removedProfileIdsRef.current.indexOf(recId) === -1 &&
                !updatedRecommendations.some(
                  (visibleRec: any) => Number(visibleRec.id) === recId
                ) &&
                !connectionRequests[rec.id]
              );
            }
          );

          const finalList = nextRecommendation
            ? [...updatedRecommendations, nextRecommendation]
            : updatedRecommendations;

          setVisibleRecommendations(finalList);
          visibleRecommendationsRef.current = finalList;
          FlashMessage("Profile removed successfully", "success");
        } else {
          FlashMessage(response.message || "Failed to remove profile", "error");
        }
      })
      .catch((error) => {
        FlashMessage("Error removing profile", "error");
        console.error("Profile removal error:", error);
      });
  };

  // Load more recommendations when visible count drops below 3
  useEffect(() => {
    if (
      visibleRecommendations.length < 3 &&
      candidateRecommendations.length > visibleRecommendations.length
    ) {
      loadMoreRecommendations();
    }

    // eslint-disable-next-line
  }, [
    visibleRecommendations.length,
    candidateRecommendations,
    connectionRequests,
  ]);

  // Reset visible recommendations when new recommendations are loaded
  useEffect(() => {
    if (
      candidateRecommendations.length > 0 &&
      visibleRecommendations.length === 0
    ) {
      const initialVisible = candidateRecommendations
        .filter(
          (rec: any) =>
            removedProfileIdsRef.current.indexOf(Number(rec.id)) === -1
        )
        .slice(0, 3);
      setVisibleRecommendations(initialVisible);
    }
  }, [candidateRecommendations, visibleRecommendations.length]);

  // Debug useEffect to monitor visibleRecommendations changes
  useEffect(() => {
    // Keep ref in sync
    visibleRecommendationsRef.current = visibleRecommendations;
  }, [visibleRecommendations]);

  let social_links = {};
  if (tokenData?.profile?.social_links) {
    try {
      social_links = JSON.parse(tokenData.profile.social_links);
    } catch (e) {
      social_links = {};
    }
  }

  type ProfessionalPassions = {
    early_career?: any;
    mid_career?: any;
    recent_life?: any;
  };

  let professional_passions: ProfessionalPassions = {};
  if (tokenData?.profile?.early_career) {
    professional_passions.early_career = tokenData.profile.early_career;
  }
  if (tokenData?.profile?.mid_career) {
    professional_passions.mid_career = tokenData.profile.mid_career;
  }
  if (tokenData?.profile?.recent_life) {
    professional_passions.recent_life = tokenData.profile.recent_life;
  }

  let profileData = {
    first_name: "",
    last_name: "",
    email: "",
    contact_info: "",
    jobTitle: null,
    location: null,
    work_status: null,
    gender: null,
    gender_optional: "",
    gender_pronoun: "",
    open_to_remote: 0,
    open_to_other: 0,
    slug: "",
  };

  let jobTitle: { value: number | string; label: string } | null = null;

  if (_.get(tokenData, "profile.job_title_custom")) {
    const customJobTitle = _.get(tokenData, "profile.job_title_custom");
    if (customJobTitle) {
      jobTitle = {
        label: customJobTitle,
        value: customJobTitle,
      };
    }
  } else {
    const jobTitleId = _.get(tokenData, "profile.jobTitle.id", 0);
    const jobTitleLabel = _.get(tokenData, "profile.jobTitle.title", "");
    if (jobTitleId && jobTitleLabel) {
      jobTitle = {
        label: jobTitleLabel,
        value: jobTitleId,
      };
    }
  }

  if (tokenData) {
    profileData = {
      first_name: tokenData?.first_name || "",
      last_name: tokenData?.last_name || "",
      email: tokenData?.email || "",
      contact_info: tokenData?.contact_info || "",
      jobTitle: jobTitle as any,
      location: tokenData?.profile?.location
        ? {
          value: tokenData.profile.location.id,
          label: tokenData.profile.location.location,
        } as any
        : null,
      gender: getGenderSelectOption(tokenData?.gender) as any,
      gender_pronoun: tokenData?.gender_pronoun,
      gender_optional: tokenData?.gender_optional,
      work_status: tokenData?.profile?.work_status
        ? {
          value: tokenData.profile.work_status,
          label: tokenData.profile.work_status,
        }
        : null as any,
      open_to_remote: tokenData?.profile?.open_to_remote,
      open_to_other: tokenData?.profile?.open_to_other,
      slug: tokenData?.slug,
    };
  }

  const services = tokenData?.services;
  // services can be an array or undefined/null
  const servicesInfo = Array.isArray(services)
    ? services.map((service) => ({
      services: service.service_name
        ? service.service_name
          .split(",")
          .map((s: string) => ({ name: s.trim() }))
        : [],
      isRemote: service.isRemote,
      // Normalize location: set to null if empty to show placeholder
      location: service?.serviceLocation?.id && service?.serviceLocation?.location
        ? {
          value: service.serviceLocation.id,
          label: service.serviceLocation.location,
        }
        : null,
      pricingOption: service.pricingOption,
      currency: service.currency
        ? { label: service.currency, value: -1 }
        : null,
      // Normalize hourlyRate: set to null if empty to show placeholder
      hourlyRate: service.hourlyRate
        ? {
          label: `$${service.hourlyRate}`,
          value: Number(service.hourlyRate),
        }
        : null,
    }))
    : [];

  const videoStudioReducer = useSelector(({ candidate }: rootReducersState) =>
    _.get(candidate, "videoStudio.list.data", [])
  );

  /** Public profile payload includes profileVideos; avoid a separate GET /video-studio/ call. */
  const videos = publicPage
    ? _.get(tokenData, "profileVideos", []) || []
    : videoStudioReducer;

  useEffect(() => {
    if (!publicPage) {
      dispatch(videoStudioListRequest({}));
    }

    // eslint-disable-next-line
  }, [publicPage]);

  useEffect(() => {
    if (publicPage || !tokenData?.id) return;
    dispatch(
      getProfileRecommendationsRequest({
        userId: tokenData.id,
        page: 1,
        pageSize: 15,
      })
    );
  }, [dispatch, publicPage, tokenData?.id]);

  const profileImageData = {
    profile_image: tokenData?.profile_image,
    profile_video: videos?.filter((item: any) => item?.is_primary === 1),
  };

  const sessionUserId = _.get(sessionReducer, "currentUser.id", null);
  const profileCandidateId = _.get(tokenData, "id", null);
  const isOwnProfile = Boolean(
    sessionUserId &&
    profileCandidateId &&
    Number(sessionUserId) === Number(profileCandidateId)
  );

  const [mobileActiveTab, setMobileActiveTab] = useState<string>("overview");
  const [openSection, setOpenSection] = useState<ProfileTaskKey | null>(null);

  const clearOpenSection = () => setOpenSection(null);

  const handleCompleteTask = (key: ProfileTaskKey) => {
    const sectionIds: Partial<Record<ProfileTaskKey, string>> = {
      education: "profile-education",
      experience: "profile-experience",
      skills: "profile-skills",
      projects: "profile-projects",
      resume: "profile-resume",
      passion: "profile-passion",
      values: "profile-values",
    };
    const targetId = sectionIds[key];
    if (targetId) {
      document.getElementById(targetId)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    setOpenSection(key);
  };

  if (publicPage && profileNotFound && !profileLoading) {
    return (
      <NotFoundPage
        heading="Oops!"
        title="We cannot find the profile you are looking for."
        buttonText="Go to Home"
        redirectTo={appRoutes.home.path}
      />
    );
  }

  if (publicPage && profileLoading) {
    return <Spinner visible={profileLoading} loadingTip="Loading profile...">
      <div className="profile-container-loader"></div>
    </Spinner>;
  }

  return (
    <Spinner visible={profileLoading}>
      <div className="profile-container">
        {/* profile header */}
        <div className="profile-header">
          <Banner tokenData={tokenData} publicPage={publicPage} />
          <CandidateInfo
            profileData={profileData}
            profileImageData={profileImageData}
            publicPage={publicPage}
            candidate={tokenData}
            openMediaUploadRequest={
              openSection === "video" || openSection === "photo"
            }
            onOpenMediaUploadHandled={clearOpenSection}
            openEditRequest={openSection === "basic"}
            onOpenEditHandled={clearOpenSection}
          />
        </div>

        {/* activities section */}
        <div
          className="activity-wrapper-mobile"
          data-active-tab={mobileActiveTab}
        >
          <div
            className={`tab-wrapper ${mobileActiveTab === "overview" ? "active" : ""
              }`}
            onClick={() => setMobileActiveTab("overview")}
          >
            Profile Overview
          </div>
          <div
            className={`tab-wrapper ${mobileActiveTab === "activity" ? "active" : ""
              }`}
            onClick={() => setMobileActiveTab("activity")}
          >
            {publicPage ? "Activity" : "My Activity"}
          </div>
        </div>

        <div className="activity-wrapper">
          <div className="left-col">
            {!publicPage && <ProfileConnections />}
            <div className="accordion-wrapper">
              <PersonalValues
                personal_values={tokenData?.profile?.personal_values}
                isPublicPage={publicPage}
                openRequest={openSection === "values"}
                onOpenHandled={clearOpenSection}
              />
              <ServicesSection servicesData={servicesInfo} isPublicPage={publicPage} />
              <ProjectsSection
                projects={tokenData?.projects}
                isPublicPage={publicPage}
                openRequest={openSection === "projects"}
                onOpenHandled={clearOpenSection}
              />
              <EducationSection
                education={tokenData?.education}
                isPublicPage={publicPage}
                openRequest={openSection === "education"}
                onOpenHandled={clearOpenSection}
              />
              <AccomplishmentsSection
                accomplishments={tokenData?.accomplishments}
                certificates={tokenData?.candidateCertificates}
                tokenData={tokenData}
                isPublicPage={publicPage}
              />
              <SkillsSection
                skills={tokenData?.skills}
                isPublicPage={publicPage}
                openRequest={openSection === "skills"}
                onOpenHandled={clearOpenSection}
              />
              <ResumeSection
                resumeData={
                  tokenData?.resumes?.length
                    ? tokenData.resumes
                    : tokenData?.profile?.resume_file
                      ? [{ resume_file: tokenData.profile.resume_file } as any]
                      : []
                }
                userProfileData={tokenData?.profile}
                isPublicPage={publicPage}
                openRequest={openSection === "resume"}
                onOpenHandled={clearOpenSection}
              />
              <SocialLinksSection social_links={social_links} isPublicPage={publicPage} />
              <RecommendationsSection
                isPublicPage={publicPage}
                isOwnProfile={isOwnProfile}
                profileCandidateId={profileCandidateId}
                profileOwner={{
                  id: tokenData?.id,
                  first_name: tokenData?.first_name,
                  last_name: tokenData?.last_name,
                  profile_image: tokenData?.profile_image,
                }}
                initialRecommendations={
                  _.get(tokenData, "recommendation", null) ||
                  _.get(tokenData, "profile.recommendation", null)
                }
              />
            </div>
          </div>
          <div className="right-col">
            {!publicPage && (
              <ProfileProgressCard
                tokenData={tokenData}
                hasPrimaryVideo={Boolean(profileImageData?.profile_video?.length)}
                onCompleteTask={handleCompleteTask}
              />
            )}
            <div className="feed-wrapper">
              <div className="feed-title">
                <b>{publicPage ? "Activity" : "My Activity"}</b>
                {!publicPage && (
                  <button
                    className="create-post"
                    onClick={() => (navigate as any)((appRoutes as any).home.path)}
                  >
                    <span>Create a post</span>
                    <img src={PlusIcon} alt="plus" />
                  </button>
                )}
              </div>
              <div className="feed-tab-wrapper">
                {[
                  {
                    key: "post",
                    label: "Posts",
                    activeIcon: <PostIcon />,
                    inactiveIcon: <PostIconGray />,
                  },
                  {
                    key: "video",
                    label: "Videos",
                    activeIcon: <VideoIconPink />,
                    inactiveIcon: <VideoIcon />,
                  },
                  {
                    key: "article",
                    label: "Articles",
                    activeIcon: <ArticleIconPink />,
                    inactiveIcon: <ArticleIcon />,
                  },
                ].map(({ key, label, activeIcon, inactiveIcon }) => (
                  <div
                    key={key}
                    className={`feed-tab ${activeTab === key ? "active" : ""}`}
                    onClick={() => setActiveTab(key)}
                  >
                    {activeTab === key ? activeIcon : inactiveIcon}
                    <span>{label}</span>
                  </div>
                ))}
              </div>
              <div className="feed-tab-content">
                {activeTab === "post" && (
                  <Feeds
                    isPublicPage={publicPage}
                    feeds={tokenData?.feed}
                    headerUser={{
                      name: `${tokenData?.first_name || ""} ${tokenData?.last_name || ""}`.trim(),
                      profile_image: tokenData?.profile_image,
                      isCompany: !!tokenData?.is_company,
                    }}
                  />
                )}
                {activeTab === "video" && (
                  <VideosCard
                    videos={videos}
                    profile_image={tokenData?.profile_image}
                    name={tokenData?.first_name + " " + tokenData?.last_name}
                    isPublicPage={publicPage}
                    job_title={tokenData?.profile?.jobTitle?.title || tokenData?.profile?.job_title_custom || ""}
                  />
                )}

                {activeTab === "article" && (
                  <Articles
                    isPublicPage={publicPage}
                    feeds={
                      tokenData?.articles ||
                      tokenData?.article ||
                      tokenData?.my_articles ||
                      tokenData?.article_feed
                    }
                    headerUser={{
                      name: `${tokenData?.first_name || ""} ${tokenData?.last_name || ""}`.trim(),
                      profile_image: tokenData?.profile_image,
                      isCompany: !!tokenData?.is_company,
                    }}
                  />
                )}
              </div>
            </div>
            <div className="accordion-wrapper">
              <ProfessionalPassions
                professional_passions={professional_passions}
                isPublicPage={publicPage}
                openRequest={openSection === "passion"}
                onOpenHandled={clearOpenSection}
              />
            </div>
            <div className="accordion-wrapper">
              <WorkExperience
                experiences={tokenData?.experiences}
                isPublicPage={publicPage}
                openRequest={openSection === "experience"}
                onOpenHandled={clearOpenSection}
              />

            </div>
            {visibleRecommendations.length > 0 && (
              <div className="suggested-connection-wrapper">
                <div className="suggested-connection-head">
                  <h4>Suggested Connections</h4>

                </div>
                <div className="suggested-cards-wrapper">
                  {visibleRecommendations.map(
                    (recommendation: any, index: number) => (
                      <ConnectionCard
                        key={`${recommendation.id}-${index}`}
                        index={index}
                        recommendation={recommendation}
                        connectionStatus={
                          connectionRequests[recommendation.id]
                        }
                        onConnectionUpdate={(id: number, status: any) => {
                          handleConnectionUpdate(id, status);
                        }}
                        onProfileRemove={handleProfileRemove}
                      />
                    )
                  )}
                </div>
                {candidateRecommendations.length >
                  visibleRecommendations.length && (
                    <button
                      type="button"
                      className="suggested-connection-view-more"
                      onClick={() =>
                        navigate(appRoutes.suggestedConnections.path)
                      }
                    >
                      View more
                    </button>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Spinner>
  );
}

export default NewProfileView;
